import { Check, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CTA from "@/components/CTA";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";

export default function PricingPage() {
  const { t } = useI18n();
  const tiers = t("pricing.tutoring.tiers") || [];
  const extras = t("pricing.tutoring.extras.items") || [];
  const business = t("pricing.business.tiers") || [];

  return (
    <>
      <PageHero
        eyebrow={t("pricing.hero.eyebrow")}
        title={t("pricing.hero.title")}
        description={t("pricing.hero.description")}
      />

      {/* TUTORING */}
      <section data-testid="pricing-tutoring" className="bg-brand-off py-20 md:py-28">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <SectionHeading
              eyebrow={t("pricing.tutoring.eyebrow")}
              title={t("pricing.tutoring.title")}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {tiers.map((tier, idx) => (
              <article
                key={idx}
                data-testid={`tier-${idx + 1}`}
                className={`relative rounded-2xl p-7 md:p-8 flex flex-col soft-shadow soft-shadow-hover hover-lift ${tier.highlight ? "bg-brand-charcoal text-brand-off border border-white/10" : "bg-white border border-black/[0.06]"}`}
              >
                {tier.highlight && (
                  <span className="absolute -top-3 left-7 inline-block rounded-full bg-brand-cyan text-brand-charcoal text-[10.5px] tracking-[0.18em] uppercase font-semibold px-3 py-1">
                    Recommended
                  </span>
                )}
                <h3 className={`text-[20px] font-medium tracking-tight ${tier.highlight ? "text-brand-off" : "text-brand-charcoal"}`}>
                  {tier.name}
                </h3>
                <p className={`mt-2 text-[13.5px] leading-relaxed min-h-[58px] ${tier.highlight ? "text-white/65" : "text-brand-muted"}`}>
                  {tier.desc}
                </p>

                <div className="mt-6 pt-5 border-t border-current/10">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-[28px] font-light tracking-tight ${tier.highlight ? "text-brand-off" : "text-brand-charcoal"}`}>
                      {tier.single}
                    </span>
                    <span className={`text-[13px] ${tier.highlight ? "text-white/55" : "text-brand-muted"}`}>
                      {t("common.perHour")}
                    </span>
                  </div>
                  <div className={`mt-2 text-[13px] ${tier.highlight ? "text-white/70" : "text-brand-muted"}`}>
                    4-pack: <span className={tier.highlight ? "text-brand-cyan" : "text-brand-blue"}>{tier.pack}</span>{" "}
                    <span className="opacity-70">({tier.packPerHour})</span>
                  </div>
                </div>

                <ul className={`mt-6 space-y-2 text-[13.5px] ${tier.highlight ? "text-white/85" : "text-brand-charcoal/90"}`}>
                  {(tier.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check className={`w-3.5 h-3.5 mt-1 shrink-0 ${tier.highlight ? "text-brand-cyan" : "text-brand-blue"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-7 pt-5 border-t border-current/10">
                  <Link
                    to="/for-students"
                    data-testid={`tier-${idx + 1}-cta`}
                    className={`inline-flex items-center gap-1.5 text-[13.5px] font-medium ${tier.highlight ? "text-brand-cyan hover:text-white" : "text-brand-charcoal hover:text-brand-blue"} transition-colors`}
                  >
                    {t("common.requestLesson")} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <p className="mt-10 text-[13.5px] text-brand-muted max-w-3xl leading-relaxed">
            {t("pricing.tutoring.note")}
          </p>

          {/* EXTRAS */}
          <div className="mt-16 rounded-2xl bg-white border border-black/[0.06] p-8 soft-shadow" data-testid="pricing-extras">
            <p className="eyebrow mb-5">{t("pricing.tutoring.extras.title")}</p>
            <ul className="divide-y divide-brand-line/80">
              {extras.map((it, i) => (
                <li key={i} className="py-4 flex items-center justify-between gap-6">
                  <span className="text-[15.5px] text-brand-charcoal">{it.name}</span>
                  <span className="text-[14px] text-brand-muted shrink-0">{it.price}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* BUSINESS */}
      <section data-testid="pricing-business" className="bg-brand-paper py-20 md:py-28 border-t border-brand-line">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("pricing.business.eyebrow")}
            title={t("pricing.business.title")}
            description={t("pricing.business.description")}
            className="mb-12"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {business.map((b, idx) => (
              <article
                key={idx}
                data-testid={`bizpricing-${idx + 1}`}
                className="group rounded-2xl bg-white border border-black/[0.06] p-7 md:p-8 soft-shadow soft-shadow-hover hover-lift flex flex-col"
              >
                <h3 className="text-[19px] font-medium text-brand-charcoal leading-tight">{b.name}</h3>
                <div className="mt-3 text-[24px] font-light tracking-tight text-brand-charcoal">{b.price}</div>
                <p className="mt-4 text-[14.5px] text-brand-muted leading-relaxed flex-1">{b.desc}</p>
                <div className="mt-6 pt-4 border-t border-brand-line/80">
                  <Link
                    to={b.to}
                    data-testid={`bizpricing-${idx + 1}-cta`}
                    className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-charcoal group-hover:text-brand-blue transition-colors"
                  >
                    {b.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* NOTE */}
      <section data-testid="pricing-note" className="bg-brand-off py-16">
        <div className="container-x">
          <div className="rounded-2xl bg-brand-charcoal text-brand-off p-8 md:p-12">
            <p className="eyebrow-light mb-4">{t("pricing.note.title")}</p>
            <p className="text-[16px] leading-[1.75] text-white/80 max-w-3xl">{t("pricing.note.text")}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <CTA to="/for-students" variant="solid-light" testid="pricing-note-cta-students" arrow>
                {t("common.seeStudents")}
              </CTA>
              <CTA to="/audit" variant="light-outline" testid="pricing-note-cta-audit" arrow>
                {t("common.bookAudit")}
              </CTA>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
